import React, { useEffect, useState } from "react";
import { studentFeeReport } from "../../../api/Student";
import { SweetAlertShow } from "../../master/Master";
import { useNavigate } from "react-router-dom";

export default function StudentFeeReport({
  filters,
  setLoading,
  inr,
  buildQuery,
}) {
  const navigate = useNavigate();
  const [data, setData] = useState({});

  const getStudentFeeReport = async () => {
    setLoading(true);

    try {
      const response = await studentFeeReport(buildQuery());

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No data found.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentFeeReport();
  }, [filters]);

  // ===========================
  // Safe number helper (API kabhi string, kabhi number bhejta hai)
  // ===========================
  const num = (v) => {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };

  const pct = (paid, final) =>
    final > 0 ? Math.min(Math.round((paid / final) * 100), 100) : 0;

  // ===========================
  // Module Wise Data
  // ===========================

  const modules = [
    {
      key: "batch",
      title: "Batch Fee",
      icon: "ri-graduation-cap-line",
      color: "primary",
      raw: data?.batch_fee,
      linkState: { batch_id: "only", session_id: filters?.session_id },
    },
    {
      key: "hostel",
      title: "Hostel Fee",
      icon: "ri-hotel-bed-line",
      color: "success",
      raw: data?.hostel_fee,
      linkState: { hostel_id: "only", session_id: filters?.session_id },
    },
    {
      key: "transport",
      title: "Transport Fee",
      icon: "ri-bus-line",
      color: "warning",
      raw: data?.transport_fee,
      linkState: { bus_id: "only", session_id: filters?.session_id },
    },
    {
      key: "library",
      title: "Library Fee",
      icon: "ri-book-2-line",
      color: "info",
      raw: data?.library_fee,
      linkState: { library_id: "only", session_id: filters?.session_id },
    },
  ].map((m) => {
    const students = num(m.raw?.students);
    const totalFee = num(m.raw?.total_fee);
    const totalDiscount = num(m.raw?.total_discount);
    const finalFee = num(m.raw?.final_fee);
    const totalPaid = num(m.raw?.total_paid);
    const totalDue = num(m.raw?.total_due);

    return {
      ...m,
      students,
      totalFee,
      totalDiscount,
      finalFee,
      totalPaid,
      totalDue,
      collectedPct: pct(totalPaid, finalFee),
      hasData: students > 0 || totalFee > 0,
    };
  });

  const summary = {
    students: num(data?.total?.students),
    totalFee: num(data?.total?.total_fee),
    totalDiscount: num(data?.total?.total_discount),
    finalFee: num(data?.total?.final_fee),
    totalPaid: num(data?.total?.total_paid),
    totalDue: num(data?.total?.total_due),
  };

  const collectedPct = pct(summary.totalPaid, summary.finalFee);

  return (
    <>
      {/* ===================== Hero Summary ===================== */}
      <div className="row g-3 mb-4">
        {/* Students */}
        <div className="col-md-3">
          <div className="card border-0 shadow-sm rounded-4 h-100 bg-gradient bg-primary text-white overflow-hidden position-relative">
            <div className="card-body position-relative" style={{ zIndex: 1 }}>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <small
                  className="text-white-50 fw-semibold text-uppercase"
                  style={{ letterSpacing: "0.5px" }}
                >
                  Total Students
                </small>
                <i className="ri-group-line fs-3 opacity-75"></i>
              </div>
              <h2 className="mb-0 fw-bold">{summary.students}</h2>
              <small className="text-white-50">Unique across all modules</small>
            </div>
            <i
              className="ri-group-fill position-absolute"
              style={{ right: -10, bottom: -20, fontSize: 100, opacity: 0.12 }}
            ></i>
          </div>
        </div>

        {/* Payable Fee */}
        <div className="col-md-5">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6
                  className="fw-bold mb-0 text-uppercase text-muted"
                  style={{ letterSpacing: "0.5px", fontSize: 12 }}
                >
                  Payable Fee Breakdown
                </h6>
                <div
                  className="bg-primary-subtle text-primary rounded-3 d-flex align-items-center justify-content-center"
                  style={{ width: 42, height: 42 }}
                >
                  <i className="ri-money-rupee-circle-line fs-5"></i>
                </div>
              </div>

              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Total Fee</span>
                <strong>{inr(summary.totalFee)}</strong>
              </div>

              <div className="d-flex justify-content-between text-danger mb-2">
                <span>Discount</span>
                <strong>- {inr(summary.totalDiscount)}</strong>
              </div>

              <hr className="my-2" />

              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">Final Payable</span>
                <span className="fw-bold text-success fs-4">
                  {inr(summary.finalFee)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Received */}
        <div
          className="col-md-2 cursor-pointer"
          onClick={() => {
            navigate(`/fees/transactions`);
          }}
        >
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <div
                className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                style={{ width: 52, height: 52 }}
              >
                <i className="ri-checkbox-circle-line fs-3"></i>
              </div>
              <h5 className="mb-0 fw-bold">{inr(summary.totalPaid)}</h5>
              <small className="text-muted">Received</small>
            </div>
          </div>
        </div>

        {/* Due */}
        <div className="col-md-2">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <div
                className="bg-warning-subtle text-warning rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                style={{ width: 52, height: 52 }}
              >
                <i className="ri-time-line fs-3"></i>
              </div>
              <h5 className="mb-0 fw-bold">{inr(summary.totalDue)}</h5>
              <small className="text-muted">Due</small>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== Overall Progress ===================== */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h6 className="mb-0 fw-bold">Fee Collection Progress</h6>
              <small className="text-muted">
                {inr(summary.totalPaid)} received of {inr(summary.finalFee)}
              </small>
            </div>
            <span
              className={`badge px-3 py-2 fs-6 ${
                collectedPct >= 75
                  ? "bg-success"
                  : collectedPct >= 40
                    ? "bg-warning text-dark"
                    : "bg-danger"
              }`}
            >
              {collectedPct}%
            </span>
          </div>

          <div className="progress rounded-pill" style={{ height: 10 }}>
            <div
              className={`progress-bar rounded-pill ${
                collectedPct >= 75
                  ? "bg-success"
                  : collectedPct >= 40
                    ? "bg-warning"
                    : "bg-danger"
              }`}
              style={{
                width: `${collectedPct}%`,
                transition: "width .5s ease",
              }}
            />
          </div>
        </div>
      </div>

      {/* ===================== Module Wise ===================== */}
      <div className="row g-3  mb-4">
        {modules.map((item) => (
          <div
            className="col-xl-3 col-md-6 cursor-pointer"
            key={item.key}
            onClick={() => {
              navigate(`/all/student/fee/report`, { state: item?.linkState });
            }}
          >
            <div
              className={`card border-0 shadow-sm rounded-4 h-100 border-start border-4 border-${item.color} ${
                !item.hasData ? "opacity-75" : ""
              }`}
            >
              <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pb-0">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className={`bg-${item.color}-subtle text-${item.color} rounded-3 d-flex align-items-center justify-content-center`}
                    style={{ width: 38, height: 38 }}
                  >
                    <i className={`${item.icon} fs-5`}></i>
                  </div>
                  <h6 className="mb-0 fw-bold">{item.title}</h6>
                </div>
              </div>

              <div className="card-body">
                {item.hasData ? (
                  <>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted small">Students</span>
                      <strong>{item.students}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted small">Total Fee</span>
                      <strong>{inr(item.totalFee)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2 text-danger">
                      <span className="small">Discount</span>
                      <strong>- {inr(item.totalDiscount)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted small">Payable</span>
                      <strong className="text-primary">
                        {inr(item.finalFee)}
                      </strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span className="small">Received</span>
                      <strong>{inr(item.totalPaid)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-3 text-warning">
                      <span className="small">Due</span>
                      <strong>{inr(item.totalDue)}</strong>
                    </div>

                    <div
                      className="progress rounded-pill"
                      style={{ height: 6 }}
                    >
                      <div
                        className={`progress-bar bg-${item.color} rounded-pill`}
                        style={{ width: `${item.collectedPct}%` }}
                      />
                    </div>
                    <small className="text-muted d-block text-end mt-1">
                      {item.collectedPct}% collected
                    </small>
                  </>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="ri-inbox-line fs-2 d-block mb-2 opacity-50"></i>
                    <small>No data available</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
